/**
 * The client app's phone → OTP seam (BP-01 §6 `client /login`).
 *
 * ## There is no OTP endpoint yet, and this file does not pretend there is
 *
 * BP-01 §5 defines **no** client OTP route: the API surface is
 * `/auth/login`, `/auth/logout`, `/auth/token`, `/auth/refresh`,
 * `/auth/totp/{enrol,verify}`, `/me`, `/tenant`, `/outlets`, `/users*`,
 * `/settings/{key}`, `/secrets/{key}`, `/events`, `/healthz`, `/readyz` — and
 * nothing else. §6 calls this screen a "phone → OTP placeholder (FakeMessenger
 * prints code in dev)" and §14's first open item leaves the transport to BP-04
 * ("WhatsApp template via BP-04; until then FakeMessenger only").
 *
 * So this module is a **local placeholder seam**. It issues and checks codes in
 * the browser, talks to no server, and grants no api credential. The
 * `PhoneOtpForm` states (idle, code-sent, error) are real and tested against it.
 *
 * ## BP-11 R17 replaced it, and the placeholder stayed
 *
 * `createApiOtpTransport` below is the real one, against BP-04's
 * `/auth/client/otp/{request,verify}`, and `createClientDeps()` uses it. It
 * is what makes `/settings/notifications`, `/me/bookings` and R9's deep link
 * reachable by somebody who is not mid-booking — and what makes R17's
 * account deletion reachable at all, since erasing your own record needs a
 * session that proves it is yours.
 *
 * **`devCode` is always `null` on the api transport**, and that is the api
 * being right rather than this file being lazy: BP-04 §9 *redacts* the code
 * from the message ledger, so there is nowhere to read it back from. The
 * field stays on the type because `createPlaceholderOtpTransport` still
 * fills it, and the placeholder still exists for the unit tests of every
 * screen that renders a code form without a server.
 */

/** E.164, as the api's `UserIn.phone_e164` pattern spells it. */
export const PHONE_PATTERN = /^\+[1-9][0-9]{1,14}$/;

/** §6's placeholder codes are six digits, like the TOTP ones (R7). */
export const OTP_LENGTH = 6;

/** A pending challenge: which phone it is for, and (in dev) the code itself. */
export interface OtpChallenge {
  /** Opaque to the UI. BP-04 replaces it with the server's id. */
  challengeId: string;
  phone: string;
  /**
   * The code, surfaced so a developer (and §11's e2e) can complete the journey
   * without a messaging provider. BP-04 must return `null` outside `dev`.
   */
  devCode: string | null;
}

/** Who the placeholder "logs in": a local identity, not an api session. */
export interface OtpIdentity {
  phone: string;
  /** ARCHITECTURE §3 Auth's `client` role — the only one this flow can grant. */
  role: 'client';
}

/** The seam BP-04 reimplements against the real transport. */
export interface OtpTransport {
  /** Start a challenge for `phone`. Rejects with `InvalidPhone` on a bad number. */
  request: (phone: string) => Promise<OtpChallenge>;
  /** Check `code`. Rejects with `InvalidOtp` when it does not match. */
  verify: (challenge: OtpChallenge, code: string) => Promise<OtpIdentity>;
}

/** The phone number is not E.164. */
export class InvalidPhone extends Error {
  constructor() {
    super('phone number must be in E.164 form, e.g. +919820000000');
    this.name = 'InvalidPhone';
  }
}

/** The code does not match the challenge. */
export class InvalidOtp extends Error {
  constructor() {
    super('that one-time code is not valid');
    this.name = 'InvalidOtp';
  }
}

/** Six digits derived from the phone number, so a dev run is reproducible. */
export function placeholderCodeFor(phone: string): string {
  let hash = 0;
  for (const character of phone) {
    hash = (hash * 31 + character.charCodeAt(0)) % 1_000_000;
  }
  return String(hash).padStart(OTP_LENGTH, '0');
}

/**
 * The in-browser placeholder. `surfaceCode` decides whether `devCode` is filled
 * — `true` in the `dev` profile and in the e2e journeys, `false` anywhere a real
 * transport would be expected.
 */
export function createPlaceholderOtpTransport(surfaceCode: boolean): OtpTransport {
  let counter = 0;
  return {
    request: (phone) => {
      if (!PHONE_PATTERN.test(phone)) {
        return Promise.reject(new InvalidPhone());
      }
      counter += 1;
      const code = placeholderCodeFor(phone);
      return Promise.resolve({
        challengeId: `placeholder-${String(counter)}`,
        phone,
        devCode: surfaceCode ? code : null,
      });
    },
    verify: (challenge, code) => {
      if (code !== placeholderCodeFor(challenge.phone)) {
        return Promise.reject(new InvalidOtp());
      }
      return Promise.resolve({ phone: challenge.phone, role: 'client' });
    },
  };
}

/** The two calls `createApiOtpTransport` needs from a transport. */
export interface OtpRoutes {
  /** `POST /auth/client/otp/request` — returns when the code is on its way. */
  request: (phone: string) => Promise<unknown>;
  /** `POST /auth/client/otp/verify` — opens the session cookie on success. */
  verify: (phone: string, code: string) => Promise<unknown>;
}

/**
 * The real transport: BP-04 R14's OTP pair, behind this file's interface.
 *
 * **No `challengeId`.** BP-04 keys the challenge server-side on
 * `otp:{tenant}:{phone_hash}` and takes the number again on verify, so there
 * is no handle for a client to hold. The field stays on `OtpChallenge` for
 * the placeholder, and this transport echoes the phone into it — which is
 * what the screen actually reads it for.
 *
 * **The rejections are this file's, not the api's.** A `request` that fails
 * because the number did not parse and one that fails because the salon is
 * unreachable are the same answer to the person typing: the number did not
 * work. `InvalidPhone` and `InvalidOtp` keep `PhoneOtpForm` free of any
 * knowledge of RFC 9457.
 */
export function createApiOtpTransport(routes: OtpRoutes): OtpTransport {
  return {
    request: async (phone) => {
      if (!PHONE_PATTERN.test(phone)) {
        throw new InvalidPhone();
      }
      try {
        await routes.request(phone);
      } catch {
        throw new InvalidPhone();
      }
      return { challengeId: phone, phone, devCode: null };
    },
    verify: async (challenge, code) => {
      try {
        await routes.verify(challenge.phone, code);
      } catch {
        throw new InvalidOtp();
      }
      return { phone: challenge.phone, role: 'client' };
    },
  };
}
